import React, { useEffect, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Palmtree, CalendarDays, UserPlus, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { KpiDetailDialog } from "@/components/KpiDetailDialog";

const CARDS = [
  ["new_ue", "AVGS: neu angelegt (UE)"], ["billed_ue", "Abgerechnete UE"], ["wb_tn", "FbW: neue Kunden"], ["wb_classes", "FbW: neue Klassen"],
  ["wb_min_tn", "FbW: Teilnehmer gesamt"], ["completed_coachings", "Abgeschlossene Coachings"], ["docs", "Vollständige Unterlagen"],
];
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const shift = (m, d) => { const dt = new Date(Number(m.slice(0, 4)), Number(m.slice(5)) - 1 + d, 1); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`; };
const num = (v) => Number(v || 0).toLocaleString("de-DE", { maximumFractionDigits: 1 });

function Goal({ k, label, x, monthName, onOpen }) {
  const pct = x.pct;
  const bar = pct == null ? "bg-slate-300" : pct >= 100 ? "bg-emerald-600" : pct >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <button type="button" onClick={onOpen} className="text-left border border-slate-200 bg-white p-5 hover:border-slate-900 hover:shadow-sm transition-[border-color,box-shadow] cursor-pointer" title="Details anzeigen" data-testid={`team-kpi-${k}`}>
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-start justify-between gap-2"><span>{label} · {monthName}</span><InfoTip k={k} testId={`team-kpi-info-${k}`} /></div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className="font-heading text-4xl font-light tracking-tighter text-slate-900" data-testid={`team-kpi-${k}-actual`}>{x.available === false ? "—" : num(x.actual)}</span>
        <span className="text-sm text-slate-400">/ {x.goal == null ? "Ziel fehlt" : `${num(x.goal)} ${x.unit}`}</span>
      </div>
      <div className="h-2 bg-slate-100 mt-3"><div className={`h-full ${bar} transition-all duration-700`} style={{ width: `${Math.min(pct || 0, 100)}%` }} /></div>
      <div className="text-xs text-slate-500 mt-2">
        {x.available === false ? "Daten fehlen" : pct == null ? "Kein Monatsziel unter „Träger“ hinterlegt" : `${num(pct)} % erreicht`}
        {k === "docs" && x.relevant > 0 && ` · ${x.complete} von ${x.relevant} Fällen erledigt`}
      </div>
      <div className="text-[11px] text-slate-400 mt-2 underline underline-offset-2">Details anzeigen</div>
    </button>
  );
}

export default function TeamDashboard({ firstName, isAdmin }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [d, setD] = useState(null);
  const [detail, setDetail] = useState(null);
  const reload = () => api.get(`/team-dashboard?month=${month}`).then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { setD(null); reload(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  if (d === false) return null;
  const monthName = `${MONTHS[Number(month.slice(5)) - 1]} ${month.slice(0, 4)}`;
  return (
    <section className="mb-12" data-testid="team-dashboard">
      <KpiDetailDialog kpi={detail?.[0]} label={detail?.[1]} month={month} monthName={monthName} onClose={() => setDetail(null)} onChanged={reload} />
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tighter text-slate-900" data-testid="team-welcome">Willkommen{firstName ? `, ${firstName}` : ""}</h2>
          <p className="text-sm text-slate-500 mt-1">Unser gemeinsamer Team-Erfolg im {monthName}.</p>
        </div>
        <div className="flex items-center border border-slate-200 bg-white" data-testid="team-month-picker">
          <button onClick={() => setMonth(shift(month, -1))} className="p-2 hover:bg-slate-100" data-testid="team-month-prev"><ChevronLeft size={16} /></button>
          <span className="px-3 text-sm font-medium min-w-[130px] text-center" data-testid="team-month-label">{monthName}</span>
          <button onClick={() => setMonth(shift(month, 1))} className="p-2 hover:bg-slate-100" data-testid="team-month-next"><ChevronRight size={16} /></button>
        </div>
      </div>
      {!d ? <div className="text-xs text-slate-400">Lädt…</div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {CARDS.map(([k, l]) => <Goal key={k} k={k} label={l} x={d.kpis[k]} monthName={monthName} onOpen={() => setDetail([k, l])} />)}
          </div>
          {d.goals_default && <div className="text-[11px] text-slate-400 mb-4">Es gelten die Voreinstellungen. {isAdmin ? <>Eigene Monatsziele unter <Link to="/traeger?tab=ziele" className="underline">Träger → Ziele</Link>.</> : "Eigene Monatsziele legt der Admin unter „Träger“ fest."}</div>}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <InfoList testId="team-new-tn" icon={UserPlus} title={`Neue Teilnehmer (${d.new_participants.length})`} empty="Keine neuen Teilnehmer im Monat."
              items={d.new_participants.map((p) => ({ key: p.id, to: p.kind === "avgs" ? `/avgs/${p.id}` : `/weiterbildung/kunden/${p.id}`, main: p.name, sub: `${p.kind === "avgs" ? "AVGS" : "WB"} · Start ${formatDeShort(p.start_date)}` }))} />
            <InfoList testId="team-incomplete" icon={AlertTriangle} title="Offene Unterlagen" empty="Alle Unterlagen vollständig."
              items={d.incomplete.map((c) => ({ key: c.id, to: `/avgs/${c.id}?tab=unterlagen`, main: c.name, sub: `${num(c.completeness)} % vollständig` }))} />
            <InfoList testId="team-vacations" icon={Palmtree} title="Urlaub im Team" empty="Kein Urlaub geplant."
              items={d.vacations.map((v, i) => ({ key: i, main: v.coach_name, sub: `${v.current ? "aktuell · " : ""}${formatDeShort(v.start_date)} – ${formatDeShort(v.end_date)}` }))} />
            <InfoList testId="team-holidays" icon={CalendarDays} title={`Feiertage · ${d.bundesland}`} empty="Keine Feiertage."
              items={d.holidays.map((h) => ({ key: h.date, main: h.name, sub: formatDeShort(h.date) }))} />
          </div>
        </>
      )}
    </section>
  );
}

function InfoList({ icon: Icon, title, items, empty, testId }) {
  return (
    <div className="border border-slate-200 bg-white" data-testid={testId}>
      <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5"><Icon size={12} /> {title}</div>
      {items.length === 0 ? <div className="p-4 text-xs text-slate-400">{empty}</div> : (
        <ul className="divide-y divide-slate-100 max-h-56 overflow-y-auto">{items.map((it) => (
          <li key={it.key} className="px-4 py-2 text-sm">
            {it.to ? <Link to={it.to} className="text-slate-900 hover:underline">{it.main}</Link> : <span className="text-slate-900">{it.main}</span>}
            <div className="text-[11px] text-slate-500">{it.sub}</div>
          </li>))}
        </ul>)}
    </div>
  );
}
